import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import DashboardCard from './DashboardCard';
import { AXIS_TICKS, GRID_COLOUR, TOOLTIP } from './chartTheme';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Funnel order from the API docs. The in-progress stages deepen in claret so
// the eye reads left to right as progress; Completed is the brand teal and
// Withdrawn sits last in grey.
const STAGES = [
  { key: 'PENDING', label: 'Pending', colour: '#F4D3DD' },
  { key: 'WELCOMED', label: 'Welcomed', colour: '#E08AA5' },
  { key: 'ORIENTED', label: 'Oriented', colour: '#B8456A' },
  { key: 'ACTIVE', label: 'Active', colour: '#8B173B' },
  { key: 'COMPLETED', label: 'Completed', colour: '#00999E' },
  { key: 'WITHDRAWN', label: 'Withdrawn', colour: '#C4C4C4' },
];

const MAX_LABEL_LENGTH = 28;

const truncate = (text) =>
  text.length > MAX_LABEL_LENGTH
    ? `${text.slice(0, MAX_LABEL_LENGTH - 1).trim()}…`
    : text;

function OnboardingFunnel({ cohorts }) {
  if (cohorts.length === 0) return null;

  // Cohort sizes range from thousands to single digits, so each bar is its
  // cohort's records split 100% by stage; raw counts stay in the tooltip.
  const totals = cohorts.map((cohort) =>
    STAGES.reduce(
      (sum, stage) => sum + (cohort.onboarding?.[stage.key] ?? 0),
      0
    )
  );
  const countFor = (stage, index) =>
    cohorts[index].onboarding?.[stage.key] ?? 0;

  const data = {
    labels: cohorts.map(
      (cohort, index) =>
        `${truncate(cohort.name)} (${totals[index].toLocaleString()})`
    ),
    datasets: STAGES.map((stage) => ({
      label: stage.label,
      data: cohorts.map((cohort, index) =>
        totals[index] ? (countFor(stage, index) / totals[index]) * 100 : 0
      ),
      backgroundColor: stage.colour,
      borderColor: '#FFFFFF',
      borderWidth: { right: 1 },
      barThickness: 18,
    })),
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        max: 100,
        grid: { color: GRID_COLOUR, drawBorder: false },
        ticks: { ...AXIS_TICKS, callback: (value) => `${value}%` },
      },
      y: {
        stacked: true,
        grid: { display: false, drawBorder: false },
        ticks: { ...AXIS_TICKS, color: '#374151' },
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 10, boxHeight: 10, padding: 16, ...AXIS_TICKS },
      },
      tooltip: {
        ...TOOLTIP,
        callbacks: {
          title: (items) => cohorts[items[0].dataIndex].name,
          label: (item) => {
            const stage = STAGES[item.datasetIndex];
            return ` ${stage.label}: ${countFor(
              stage,
              item.dataIndex
            ).toLocaleString()} (${item.raw.toFixed(1)}%)`;
          },
        },
      },
    },
  };

  return (
    <DashboardCard
      title="Onboarding funnel"
      subtitle="Each cohort's membership records split by onboarding stage, record count in brackets. Students who belong only through their primary cohort have no record, so counts can be lower than members."
    >
      <div style={{ height: 70 + cohorts.length * 34 }}>
        <Bar data={data} options={options} />
      </div>
    </DashboardCard>
  );
}

export default OnboardingFunnel;
